/* eslint-disable @typescript-eslint/no-explicit-any */
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Outlet } from 'react-router-dom';
import ErrorBoundary from '../components/ui/ErrorBoundary';
import CommonLayout from '../components/layout/CommonLayout';

import {
  Routes,
  PrivateResaleRoutes,
  PreviewRoutes,
  CommercialIndustrialRoutes,
  OthersRoutes,
  HdbResaleRoutes,
} from '../constants/routes';

// --- Lazy imports grouped by domain ---

// Main
const Main = lazy(() => import('../pages/Main'));

// HDB Resale
const Purchase = lazy(() => import('../pages/hdb-resale/Purchase'));
const PurchasePreview = lazy(() => import('../pages/hdb-resale/PurchasePreview'));
const Msr = lazy(() => import('../pages/hdb-resale/Msr'));
const MsrPreview = lazy(() => import('../pages/hdb-resale/MsrPreview'));
const SalesProceeds = lazy(() => import('../pages/hdb-resale/SalesProceeds'));
const SalesProceedsPreview = lazy(() => import('../pages/hdb-resale/SalesProceedsPreview'));

// Private Resale
const PurchaseResale = lazy(() => import('../pages/private-resale/PurchaseResale'));
const PurchaseResalePreview = lazy(() => import('../pages/private-resale/PurchaseResalePreview'));
const PurchaseNewProject = lazy(() => import('../pages/private-resale/PurchaseNewProject'));
const PurchaseNewProjectPreview = lazy(
  () => import('../pages/private-resale/PurchaseNewProjectPreview'),
);
const BuyerStampDuty = lazy(() => import('../pages/private-resale/BuyerStampDuty'));
const BuyerStampDutyPreview = lazy(() => import('../pages/private-resale/BuyerStampDutyPreview'));
const SalesProceed = lazy(() => import('../pages/private-resale/SalesProceed'));
const RentalYield = lazy(() => import('../pages/private-resale/RentalYield'));
const TDSR = lazy(() => import('../pages/private-resale/TDSR'));

// Commercial Industrial
const CIPurchase = lazy(() => import('../pages/commercial-industrial/PurchaseResale'));
const CIRentalYield = lazy(() => import('../pages/commercial-industrial/RentalYield'));
const CISalesProceeds = lazy(() => import('../pages/commercial-industrial/SalesProceeds'));
const CIResaleVsBuc = lazy(() => import('../pages/commercial-industrial/ResaleVsBuc'));
const CIInvestment = lazy(() => import('../pages/commercial-industrial/Investment'));

// Others
const OthersMortgageLoan = lazy(() => import('../pages/others/MortgageLoan'));
const MortgageLoanPreview = lazy(() => import('../pages/others/MortgageLoanPreview'));
const OthersPartSharePurchase = lazy(() => import('../pages/others/PartSharePurchase'));
const PartSharePurchasePreview = lazy(() => import('../pages/others/PartSharePurchasePreview'));
const OthersEquityTermLoan = lazy(() => import('../pages/others/EquityTermLoan'));
const EquityTermLoanPreview = lazy(() => import('../pages/others/EquityTermLoanPreview'));
const OthersPledgeUnpledge = lazy(() => import('../pages/others/PledgeUnpledge'));
const PledgeUnpledgePreview = lazy(() => import('../pages/others/PledgeUnpledgePreview'));
const OthersRentalStampDuty = lazy(() => import('../pages/others/RentalStampDuty'));
const RentalStampDutyPreview = lazy(() => import('../pages/others/RentalStampDutyPreview'));
const OthersRentalYield = lazy(() => import('../pages/others/RentalYield'));
const CpfUsage = lazy(() => import('../pages/others/CpfUsage'));
const CpfUsagePreview = lazy(() => import('../pages/others/CpfUsagePreview'));

// --- Helper Functions ---
const withSuspense = (Component: React.LazyExoticComponent<any>) => () => (
  <Suspense fallback={<p>...</p>}>
    <Component />
  </Suspense>
);

const withPreviewRoutes = (basePath: string, MainComponent: any, PreviewComponent: any) => [
  {
    path: basePath,
    index: true,
    Component: withSuspense(MainComponent),
  },
  {
    path: basePath.replace(/\/$/, '') + '/preview',
    index: true,
    Component: withSuspense(PreviewComponent),
  },
];

const sectionIndex = (path: string) => ({
  path,
  index: true,
  Component: withSuspense(Main),
});

// --- Routers ---
const router = createBrowserRouter([
  {
    path: Routes.ROOT,
    errorElement: <ErrorBoundary />,
    Component: CommonLayout,
    children: [
      sectionIndex(Routes.ROOT),

      // Private Resale
      { path: PrivateResaleRoutes.PURCHASE_RESALE, Component: withSuspense(PurchaseResale) },
      { path: PreviewRoutes.PURCHASE_RESALE, Component: withSuspense(PurchaseResalePreview) },
      { path: PrivateResaleRoutes.BUYER_STAMP_DUTY, Component: withSuspense(BuyerStampDuty) },
      { path: PreviewRoutes.BUYER_STAMP_DUTY, Component: withSuspense(BuyerStampDutyPreview) },
      { path: PrivateResaleRoutes.SALES_PROCEEDS, Component: withSuspense(SalesProceed) },
      { path: PrivateResaleRoutes.RENTAL_YIELD, Component: withSuspense(RentalYield) },
      { path: PrivateResaleRoutes.TDSR, Component: withSuspense(TDSR) },

      // HDB Resale Section
      {
        path: Routes.HDB_RESALE,
        element: <Outlet />,
        children: [
          sectionIndex(Routes.HDB_RESALE),
          {
            path: Routes.HDB_RESALE + HdbResaleRoutes.PURCHASE,
            children: withPreviewRoutes(
              Routes.HDB_RESALE + HdbResaleRoutes.PURCHASE,
              Purchase,
              PurchasePreview,
            ),
          },
          {
            path: Routes.HDB_RESALE + HdbResaleRoutes.MSR,
            children: withPreviewRoutes(Routes.HDB_RESALE + HdbResaleRoutes.MSR, Msr, MsrPreview),
          },
          {
            path: Routes.HDB_RESALE + HdbResaleRoutes.SALES_PROCEEDS,
            children: withPreviewRoutes(
              Routes.HDB_RESALE + HdbResaleRoutes.SALES_PROCEEDS,
              SalesProceeds,
              SalesProceedsPreview,
            ),
          },
        ],
      },

      // Private Resale Section
      {
        path: Routes.PRIVATE_RESALE,
        element: <Outlet />,
        children: [
          sectionIndex(Routes.PRIVATE_RESALE),
          {
            path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_NEW_PROJECT,
            children: withPreviewRoutes(
              Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_NEW_PROJECT,
              PurchaseNewProject,
              PurchaseNewProjectPreview,
            ),
          },
          {
            path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_RESALE,
            children: withPreviewRoutes(
              Routes.PRIVATE_RESALE + PrivateResaleRoutes.PURCHASE_RESALE,
              PurchaseResale,
              PurchaseResalePreview,
            ),
          },
          {
            path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.BUYER_STAMP_DUTY,
            children: withPreviewRoutes(
              Routes.PRIVATE_RESALE + PrivateResaleRoutes.BUYER_STAMP_DUTY,
              BuyerStampDuty,
              BuyerStampDutyPreview,
            ),
          },
          {
            path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.SALES_PROCEEDS,
            Component: withSuspense(SalesProceed),
          },
          {
            path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.RENTAL_YIELD,
            Component: withSuspense(RentalYield),
          },
          { path: Routes.PRIVATE_RESALE + PrivateResaleRoutes.TDSR, Component: withSuspense(TDSR) },
        ],
      },

      // Commercial Industrial
      {
        path: Routes.COMMERCIAL_INDUSTRIAL,
        element: <Outlet />,
        children: [
          sectionIndex(Routes.COMMERCIAL_INDUSTRIAL),
          {
            path: Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.PURCHASE_RESALE,
            Component: withSuspense(CIPurchase),
          },
          {
            path: Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.RENTAL_YIELD,
            Component: withSuspense(CIRentalYield),
          },
          {
            path: Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.SALES_PROCEEDS,
            Component: withSuspense(CISalesProceeds),
          },
          {
            path: Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.RESALE_VS_BUC,
            Component: withSuspense(CIResaleVsBuc),
          },
          {
            path: Routes.COMMERCIAL_INDUSTRIAL + CommercialIndustrialRoutes.INVESTMENT,
            Component: withSuspense(CIInvestment),
          },
        ],
      },

      // Others
      {
        path: Routes.OTHERS,
        element: <Outlet />,
        children: [
          sectionIndex(Routes.OTHERS),

          {
            path: Routes.OTHERS + OthersRoutes.MORTGAGE_LOAN,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.MORTGAGE_LOAN,
              OthersMortgageLoan,
              MortgageLoanPreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.PART_SHARE_PURCHASE,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.PART_SHARE_PURCHASE,
              OthersPartSharePurchase,
              PartSharePurchasePreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.EQUITY_TERM_LOAN,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.EQUITY_TERM_LOAN,
              OthersEquityTermLoan,
              EquityTermLoanPreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.PLEDGE_UNPLEDGE,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.PLEDGE_UNPLEDGE,
              OthersPledgeUnpledge,
              PledgeUnpledgePreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.RENTAL_STAMP_DUTY,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.RENTAL_STAMP_DUTY,
              OthersRentalStampDuty,
              RentalStampDutyPreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.BUYER_STAMP_DUTY,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.BUYER_STAMP_DUTY,
              BuyerStampDuty,
              BuyerStampDutyPreview,
            ),
          },
          {
            path: Routes.OTHERS + OthersRoutes.RENTAL_YIELD,
            Component: withSuspense(OthersRentalYield),
          },
          {
            path: Routes.OTHERS + OthersRoutes.MSR,
            children: withPreviewRoutes(Routes.OTHERS + OthersRoutes.MSR, Msr, MsrPreview),
          },
          {
            path: Routes.OTHERS + OthersRoutes.CPF_USAGE,
            children: withPreviewRoutes(
              Routes.OTHERS + OthersRoutes.CPF_USAGE,
              CpfUsage,
              CpfUsagePreview,
            ),
          },
        ],
      },
    ],
  },
]);

export default router;
